import { ApiClient } from '../client/ApiClient';
import { ApiResponse } from '../types';
import { CountryCode, getMultiTenancyHeaders } from '../utils/multiTenancyHeaders';
import { getShopifyAuthHeader, ShopifyTokenGenerator } from '../utils/shopifySessionTokens';
import { Exclusions, TermsAndConditions, ValidateCouponsRequest, ValidateCouponsResponse } from './types';

export class OnlineCouponsApiClient extends ApiClient {
  private readonly _baseUrl: string;
  private readonly _countryCode?: CountryCode;
  private readonly _shopifyTokenGenerator?: ShopifyTokenGenerator;
  private readonly _key?: string;

  constructor({ baseUrl, countryCode, key }: { baseUrl: string; countryCode: CountryCode; key?: string }) {
    super();
    this._baseUrl = `${baseUrl}`;
    this._countryCode = countryCode;
    this._key = key;
  }

  generateHeaders = async () => {
    const isInCheckoutExtensibility = this._shopifyTokenGenerator !== undefined;
    let userValidationHeaders: Record<string, string> = {};
    if (isInCheckoutExtensibility) {
      userValidationHeaders = (await getShopifyAuthHeader(this._shopifyTokenGenerator))!;
    }
    return {
      headers: {
        ...userValidationHeaders,
        ...(this._countryCode ? getMultiTenancyHeaders(this._countryCode) : {}),
      },
    };
  };

  async getTermsAndConditions(reference: string): Promise<ApiResponse<TermsAndConditions>> {
    const { headers: requestHeaders } = await this.generateHeaders();
    if (!requestHeaders) {
      return { statusCode: 500, error: new Error('Headers not valid') };
    }

    return await this._fetch<TermsAndConditions>({
      path: `${this._baseUrl}/TermsAndConditions/${reference}`,
      method: 'GET',
      headers: requestHeaders,
    });
  }

  async getExclusions(externalId: string): Promise<ApiResponse<Exclusions>> {
    const { headers: requestHeaders } = await this.generateHeaders();
    if (!requestHeaders) {
      return { statusCode: 500, error: new Error('Headers not valid') };
    }

    return await this._fetch<Exclusions>({
      path: `${this._baseUrl}/Exclusions/${externalId}`,
      method: 'GET',
      headers: requestHeaders,
    });
  }

  async validateCoupons(request: ValidateCouponsRequest): Promise<ApiResponse<ValidateCouponsResponse>> {
    const { headers: requestHeaders } = await this.generateHeaders();
    if (!requestHeaders) {
      return { statusCode: 500, error: new Error('Headers not valid') };
    }

    return await this._fetch<ValidateCouponsResponse>({
      path: `${this._baseUrl}/validate`,
      method: 'POST',
      body: JSON.stringify(request),
      headers: {
        ...requestHeaders,
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': this._key ?? '',
      },
    });
  }
}
